import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, onValue, update, remove } from "firebase/database";
import { Search, Trash2, RotateCcw, Settings, PlusCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import './Users.css';
import TrapModal from './Trap';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTrap, setShowTrap] = useState(false);
  const usersPerPage = 20;

  useEffect(() => {
    const usersRef = ref(db, 'users');
    onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const userList = Object.keys(data).map(key => ({ id: key, ...data[key] }));
        
        userList.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });

        setUsers(userList);
      }
    });
  }, []);

  const handleStatusToggle = (userId, currentStatus) => {
    const newStatus = currentStatus === 'Freeze' ? 'Active' : 'Freeze';
    update(ref(db, `users/${userId}`), { status: newStatus });
  };

  const toggleTaskActive = (userId, currentVal) => {
    update(ref(db, `users/${userId}`), { taskActive: !currentVal });
  };

  const handleAddBalance = (user) => {
    Swal.fire({
      title: `Add Balance: ${user.username}`,
      input: 'number',
      inputLabel: 'Amount in USDT',
      inputAttributes: { step: 'any' },
      background: '#1a1a1a',
      color: '#fff',
      confirmButtonColor: '#f3ba2f',
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const newBal = (parseFloat(user.balance || 0) + parseFloat(result.value)).toFixed(2);
        update(ref(db, `users/${user.id}`), { balance: newBal });
        Swal.fire('Success', 'Balance updated!', 'success');
      }
    });
  };

  const handleResetTasks = (user) => {
    Swal.fire({
      title: 'Reset Tasks?',
      text: "Move Earned to Yesterday and set Completed to 0",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f3ba2f',
    }).then((result) => {
      if (result.isConfirmed) {
        update(ref(db, `users/${user.id}`), {
          yesterdayCommission: user.earnedCommission || 0,
          earnedCommission: 0,
          completedTasks: 0
        });
        Swal.fire('Reset!', 'User tasks cleared.', 'success');
      }
    });
  };

  // ✅ Fixed openSettings to handle Password, Wallet Name and TRC Address
  const openSettings = (user) => {
    Swal.fire({
      title: `Edit: ${user.username}`,
      background: '#1a1a1a',
      color: '#fff',
      html: `
        <div style="text-align:left;">
          <label style="font-size:12px; color:#888;">Password</label>
          <input id="newPass" class="swal2-input" style="margin:10px 0; width:90%;" value="${user.password || ''}">
          <label style="font-size:12px; color:#888;">Wallet Name</label>
          <input id="newWalletName" class="swal2-input" style="margin:10px 0; width:90%;" value="${user.paymentMethod?.walletName || ''}">
          <label style="font-size:12px; color:#888;">TRC20 Address</label>
          <input id="newTRC" class="swal2-input" style="margin:10px 0; width:90%;" value="${user.paymentMethod?.address || ''}">
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Save',
      confirmButtonColor: '#f3ba2f',
      preConfirm: () => {
        return {
          password: document.getElementById('newPass').value,
          walletName: document.getElementById('newWalletName').value,
          trcAddress: document.getElementById('newTRC').value
        }
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const updates = {};
        updates[`users/${user.id}/password`] = result.value.password;
        updates[`users/${user.id}/paymentMethod/walletName`] = result.value.walletName;
        updates[`users/${user.id}/paymentMethod/address`] = result.value.trcAddress;
        
        update(ref(db), updates);
        Swal.fire('Updated!', '', 'success');
      }
    });
  };

  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  return (
    <div className="users-section">
      <header className="users-header">
        <div className="search-bar">
          <Search size={16} />
          <input type="text" placeholder="Search username..." onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="active-badge">Users: {users.length}</div>
      </header>

      <div className="table-wrapper">
        <table className="custom-table">
          <thead>
            <tr>
              <th>USERNAME</th>
              <th>GMAIL</th>
              <th>TASKS</th>
              <th>BALANCE</th>
              <th>TRAP</th>
              <th>ACTIVATE</th>
              <th>FREEZE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((u) => (
              <tr key={u.id}>
                <td className="gold-text">{u.username}</td>
                <td className="dim-text">{u.email || '---'}</td>
                <td className="task-count-cell">
                    <span className={u.completedTasks >= 25 ? 'text-green' : 'text-red-dim'}>
                        {u.completedTasks || 0}/25
                    </span>
                </td>
                <td>
                  {u.balance} <PlusCircle size={14} className="add-bal-icon" onClick={() => handleAddBalance(u)} />
                </td>
                <td>
                  <button className="set-trap-btn" onClick={() => { setSelectedUser(u); setShowTrap(true); }}>+ Trap</button>
                </td>
                <td>
                  <label className="toggle">
                    <input type="checkbox" checked={u.taskActive} onChange={() => toggleTaskActive(u.id, u.taskActive)} />
                    <span className="slider"></span>
                  </label>
                </td>
                <td>
                  <label className="toggle">
                    <input type="checkbox" checked={u.status === 'Freeze'} onChange={() => handleStatusToggle(u.id, u.status)} />
                    <span className="slider red-slider"></span>
                  </label>
                </td>
                <td>
                  <div className="action-group">
                    <button onClick={() => openSettings(u)}><Settings size={14}/></button>
                    <button onClick={() => handleResetTasks(u)}><RotateCcw size={14}/></button>
                    <button className="del-btn" onClick={() => remove(ref(db, `users/${u.id}`)) }><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showTrap && <TrapModal user={selectedUser} onClose={() => setShowTrap(false)} />}
    </div>
  );
};

export default Users;